// Temporary runtime ADA/WCAG diagnostics (development / troubleshooting).
//
// Enable by adding ?a11yDebug=1 to any page URL (or localStorage.a11yDebug
// = "1"). When enabled, every relevant event logs a "[a11y-diag]" snapshot
// to the console, and the live state is always inspectable via
// window.__a11yDiag.snapshot(). Zero overhead when disabled apart from a
// couple of counters.
type DiagState = {
  adaWcagEnabled: boolean | null;
  a11yAnnouncements: boolean | null;
  botOwnerOrganizationId: string | null;
  loggedInUserOrganizationId: string | null;
  speechSynthesisCallCount: number;
  programmaticFocusCallCount: number;
  lastAnnouncementText: string | null;
  lastAnnouncementSource: string | null;
};

const state: DiagState = {
  adaWcagEnabled: null,
  a11yAnnouncements: null,
  botOwnerOrganizationId: null,
  loggedInUserOrganizationId: null,
  speechSynthesisCallCount: 0,
  programmaticFocusCallCount: 0,
  lastAnnouncementText: null,
  lastAnnouncementSource: null,
};

function enabled(): boolean {
  try {
    return (
      new URLSearchParams(window.location.search).has("a11yDebug") ||
      window.localStorage.getItem("a11yDebug") === "1"
    );
  } catch {
    return false;
  }
}

function frontendBuildHash(): string {
  const src = Array.from(document.scripts)
    .map((s) => s.src)
    .find((s) => /\/assets\/index-[^/]+\.js/.test(s));
  return src ? src.replace(/^.*\/assets\//, "") : "dev-server";
}

function snapshot() {
  const ae = document.activeElement as HTMLElement | null;
  return {
    ...state,
    url: window.location.href,
    activeElement: ae
      ? `${ae.tagName}${ae.dataset?.testid ? `[data-testid=${ae.dataset.testid}]` : ""}`
      : null,
    ariaLiveNodeCount: document.querySelectorAll("[aria-live]").length,
    roleStatusNodeCount: document.querySelectorAll('[role="status"]').length,
    roleAlertNodeCount: document.querySelectorAll('[role="alert"]').length,
    frontendBuildHash: frontendBuildHash(),
  };
}

function log(event: string) {
  if (!enabled()) return;
  // eslint-disable-next-line no-console
  console.log(`[a11y-diag] ${event}`, snapshot());
}

// Count (and, when debugging, log) every speechSynthesis.speak call —
// the app itself never calls it, so any non-zero count is third-party.
try {
  if (
    typeof window !== "undefined" &&
    window.speechSynthesis &&
    !(window.speechSynthesis.speak as any).__a11yDiagPatched
  ) {
    const orig = window.speechSynthesis.speak.bind(window.speechSynthesis);
    window.speechSynthesis.speak = (u: SpeechSynthesisUtterance) => {
      state.speechSynthesisCallCount += 1;
      log(`speechSynthesis.speak("${(u?.text || "").slice(0, 80)}")`);
      return orig(u);
    };
    (window.speechSynthesis.speak as any).__a11yDiagPatched = true;
  }
} catch {
  /* diagnostics must never break the app */
}

export function diagSetFlags(flags: {
  adaWcagEnabled?: boolean;
  a11yAnnouncements?: boolean;
  botOwnerOrganizationId?: string | null;
  loggedInUserOrganizationId?: string | null;
}): void {
  Object.assign(state, flags);
  log("flags updated");
}

export function diagRecordFocus(source: string): void {
  state.programmaticFocusCallCount += 1;
  log(`programmatic focus: ${source}`);
}

export function diagRecordAnnouncement(text: string, source: string): void {
  state.lastAnnouncementText = text || null;
  state.lastAnnouncementSource = text ? source : state.lastAnnouncementSource;
  if (text) log(`announcement from ${source}`);
}

if (typeof window !== "undefined") {
  (window as any).__a11yDiag = { snapshot, state };
}
